import {right} from "@popperjs/core";
import {
    Key as KeyIcon,
    SecurityPass as TrustUnknownIcon,
    SecurityPass as TrustedIcon,
    ShieldBroken as CTLStatusBlacklistedIcon,
    ShieldCheck as CTLStatusWhitelistedIcon,
    WarningCircle as NotTrustedIcon
} from "iconoir-react";
import {Dispatch, SetStateAction, useEffect, useState} from 'react'
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {useTranslation} from "react-i18next";
import {type CertificateValidationResult, CTLEntryStatus, X509CertificateDetails} from "../api/admin/generated";
import {Truncate} from "../common/Truncate";
import {DateUtils} from "../utils/DateUtils";

interface CertificateColumnProps {
    value? : string
    children? : React.ReactNode
    className? : string
}

const CertificateColumn = ({value, children, className} : CertificateColumnProps) => {
    return (
        <td className={"user-select-all " + className}>{children}<Truncate value={value} maxLength={60}/></td>
    )
}

type CheckCertificateValidityCallback = (certificate : X509CertificateDetails, setCertificateValidationResult : Dispatch<SetStateAction<CertificateValidationResult | undefined>>) => void

interface CertificateRowProps {
    certificate : X509CertificateDetails
    children? : React.ReactNode
    checkCertificateValidityCallback? : CheckCertificateValidityCallback
    selected?: boolean
}

export const CertificateRow = ({certificate, children, checkCertificateValidityCallback, selected} : CertificateRowProps ) =>
{
    /**
     * The certificate validation status
     */
    const [certificateValidationResult, setCertificateValidationResult] = useState<CertificateValidationResult>();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() => {
        // logger.debug(`Checking validity of certificate ${certificate.thumbprint}`)

        if (checkCertificateValidityCallback !== undefined && setCertificateValidationResult !== undefined) {
            checkCertificateValidityCallback(certificate, setCertificateValidationResult);
        }
    }, [certificate, checkCertificateValidityCallback])

    /**
     * Determines the CSS class name for a row based on the certificate validation result.
     */
    const rowCssClass = () =>
    {
        if (certificateValidationResult?.revoked) {
            return "tr-danger"
        }

        return undefined;
    }

    const trustIcon = () =>
    {
        if (certificateValidationResult === undefined) {
            return (
                <OverlayTrigger
                    placement={right}
                    overlay={
                        <Tooltip>
                            {t("certificates.trust-unknown")}
                        </Tooltip>
                    }
                >
                    <TrustUnknownIcon className="text-secondary table-icon-width table-icon-height ms-2" />
                </OverlayTrigger>
            );
        }

        if (certificateValidationResult.valid) {
            return (
                <OverlayTrigger
                    placement={right}
                    overlay={
                        <Tooltip>
                            {t("certificates.trusted")}
                        </Tooltip>
                    }
                >
                    <TrustedIcon className="text-success table-icon-width table-icon-height ms-2"/>
                </OverlayTrigger>
            );
        }

        return (
            <OverlayTrigger
                placement={right}
                overlay={
                    <Tooltip>
                        {certificateValidationResult?.failureMessage}
                    </Tooltip>
                }
            >
                <NotTrustedIcon className="text-danger table-icon-width table-icon-height ms-2" />
            </OverlayTrigger>
        )
    }

    return (
        <tr key={certificate.thumbprint} className={`${rowCssClass()}`}>
            <CertificateColumn className="td-w-min">
                <div className="d-flex align-items-center">
                    {children}

                    {trustIcon()}

                    {certificate.keyAlias !== null && (certificate.privateKeyAccessible ?
                            <OverlayTrigger
                                placement={right}
                                overlay={
                                    <Tooltip>
                                        {t("certificates.private-key-available")}
                                    </Tooltip>
                                }
                            >
                                <KeyIcon className="text-success table-icon-width table-icon-height ms-1"/>
                            </OverlayTrigger>
                            :
                            <OverlayTrigger
                                placement={right}
                                overlay={
                                    <Tooltip>
                                        {t("certificates.private-key-not-accessible")}
                                    </Tooltip>
                                }
                            >
                                <KeyIcon className="text-danger table-icon-width table-icon-height ms-1"/>
                            </OverlayTrigger>
                    )}

                    {certificate.ctlStatus === CTLEntryStatus.Whitelisted &&
                        <OverlayTrigger
                            placement={right}
                            overlay={
                                <Tooltip>
                                    {t("certificates.ctl-trusted")}
                                </Tooltip>
                            }
                        >
                            <CTLStatusWhitelistedIcon className="text-success table-icon-width table-icon-height ms-1"/>
                        </OverlayTrigger>
                    }

                    {certificate.ctlStatus === CTLEntryStatus.Blacklisted &&
                        <OverlayTrigger
                            placement={right}
                            overlay={
                                <Tooltip>
                                    {t("certificates.ctl-not-trusted")}
                                </Tooltip>
                            }
                        >
                            <CTLStatusBlacklistedIcon className="text-danger table-icon-width table-icon-height ms-1"/>
                        </OverlayTrigger>
                    }
                </div>
            </CertificateColumn>
            {/*remove duplicate email addresses*/}
            <CertificateColumn value={[...new Set([...certificate.emailFromAltNames, ...certificate.emailFromDN])].join(", ")} className={selected ? "fw-bold" : ""} />
            <CertificateColumn value={certificate.subjectFriendly} />
            <CertificateColumn value={certificate.issuerFriendly} />
            <CertificateColumn value={certificate.serialNumberHex} />
            <CertificateColumn value={certificate.thumbprint} />
            <CertificateColumn value={certificate.thumbprintSHA1} />
            <CertificateColumn value={DateUtils.timestampToDateStringShort(certificate.notBefore)} />
            <CertificateColumn value={DateUtils.timestampToDateStringShort(certificate.notAfter)} />
            <CertificateColumn value={[...certificate.keyUsage ?? []].join(", ")} />
            <CertificateColumn value={[...certificate.extendedKeyUsage ?? []].join(", ")} />
            <CertificateColumn value={certificate.publicKeyAlgorithm} />
            <CertificateColumn value={(certificate.publicKeyLength ?? -1).toString()} />
            <CertificateColumn value={certificate.signatureAlgorithm} />
            <CertificateColumn value={certificateValidationResult?.failureMessage} className="text-danger" />
        </tr>
    )
}
